import {
  CustomCollapseCard,
  CustomImage,
  CustomPopover,
  Text,
  useText,
  warningDialog,
} from '@src/components';
import { Button, Radio, Space } from 'antd';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css/bundle';
import 'swiper/css/pagination';
import CreditCards from './CreditCards';
import { useDispatch, useSelector } from 'react-redux';
import { setPayWithLinkinEmail, setSelectStoredCard } from '@src/store/slice/mobilExpressSlice';
import React, { useEffect, useMemo, useState } from 'react';
import iconInfo from '@src/assets/icons/icon-bilgi.svg';
import dayjs from '@src/utils/day';
import { prevStep } from '@src/store/slice/cardOrderSlice';
import iconBack from '@src/assets/icons/icon-back.svg';
import classNames from 'classnames';
import iconCircle from '@src/assets/icons/info-circle-white.svg';
import { VouIdCode } from '@src/store/enums';

const SavedCreditCards = ({ handleAnotherCreditCard }) => {
  const isMobil = useMediaQuery({ query: '(max-width: 991.98px)' });
  const { currentCardOrder } = useSelector((state) => state?.cardOrder);
  const { storedCard, selectedStoredCard } = useSelector((state) => state?.mobilExpress);
  const dispatch = useDispatch();
  const [hasOtherCards, setHasOtherCards] = useState(false);
  const [showPayLater, setShowPayLater] = useState(false);
  const [afterMount, setAfterMount] = useState(false);
  const [isComplimentsVou, setIsComplimentsVou] = useState(false);

  const pleaseSelectOneOfYourStoredCards = useText('pleaseSelectOneOfYourStoredCards');
  useEffect(() => {
    if (selectedStoredCard === null && storedCard?.length > 0) {
      dispatch(setSelectStoredCard(storedCard[0].cardToken));
    }
  }, [selectedStoredCard, storedCard]);

  useEffect(() => {
    setIsComplimentsVou(
      currentCardOrder?.vouId === VouIdCode.Compliments ||
        currentCardOrder?.vouId === VouIdCode.Market,
    );
    const today = dayjs().hour(23).minute(59).second(59).millisecond(0);
    const activationDate = dayjs(currentCardOrder?.cardActivationDate)
      .hour(23)
      .minute(59)
      .millisecond(0)
      .second(59);
    setShowPayLater(activationDate.isAfter(today));
    setAfterMount(activationDate.month() > today.month() || activationDate.year() > today.year());
  }, [currentCardOrder]);

  const activeCard = useMemo(() => {
    dispatch(setSelectStoredCard(null));
    if (storedCard.length === 0) {
      return {};
    }
    const defaultFlag = storedCard.find((s) => s.unlimited_Contract && s.default_Flag);
    dispatch(setPayWithLinkinEmail(false));
    if (defaultFlag?.cardToken) {
      setHasOtherCards(true);
      dispatch(setSelectStoredCard(defaultFlag?.cardToken));
    }
    return defaultFlag;
  }, [storedCard]);

  const storedCardFilter = useMemo(() => {
    return storedCard.filter((s) => s.cardToken !== activeCard?.cardToken);
  }, [activeCard]);

  const onChange = (e) => {
    dispatch(setSelectStoredCard(e.target.value));
    dispatch(setPayWithLinkinEmail(false));
  };

  if (storedCard.length === 0) {
    return null;
  }

  const getMarginTop = () => {
    if (activeCard) return '41px';
    return isMobil ? '25px' : '0';
  };

  const getPaymentButtonText = () => {
    if (isComplimentsVou) return <Text t="confirmPayment" />;
    if (afterMount) return <Text t="continue" />;
    return <Text t="payNow" />;
  };

  const renderPayLaterButton = () =>
    isComplimentsVou ? (
      <button
        className={
          'compliments-payment-summary-later-pay compliments-payment-summary-later-pay-button'
        }
        onClick={async () => {
          if (!selectedStoredCard) {
            return warningDialog({
              title: <Text t="attention" />,
              message: pleaseSelectOneOfYourStoredCards,
            });
          }
          await window.payWhenBalanceActiveDay();
        }}
      >
        <span>
          <Text t="payLater" />
        </span>
        <CustomPopover
          id="ticketRestaurant-cardOrders-payment-newCreditCards-popover"
          placement="topRight"
          title={false}
          content={
            <div
              style={{
                width: '300px',
                color: '#3f4957',
                padding: '7px 18px',
              }}
            >
              <span>
                <Text t="onTheNextScreen" />{' '}
                <b className="compliments-bold-text">
                  <Text t="automaticCollectionOnTheDayTheBalanceBecomes" />
                </b>{' '}
                <Text t="or" />{' '}
                <b className="compliments-bold-text">
                  <Text t="paymentViaTheLinkSentToYourE" />
                </b>{' '}
                <Text t="youCanChooseOneOfTheMethods" />
              </span>
            </div>
          }
          trigger="hover"
          arrowPointAtCenter={true}
        >
          <div
            style={{
              marginRight: 0,
              marginBottom: 2,
            }}
          >
            <CustomImage src={iconCircle} />
          </div>
        </CustomPopover>
      </button>
    ) : (
      <button
        className={classNames({
          'pay-when-balance-active-day-button': true,
        })}
        style={{ backgroundColor: 'transparent', borderWidth: 0 }}
        onClick={async () => {
          if (!selectedStoredCard) {
            return warningDialog({
              title: <Text t="attention" />,
              message: pleaseSelectOneOfYourStoredCards,
            });
          }
          await window.payWhenBalanceActiveDay();
        }}
      >
        <span>
          <Text t="payLater" />
        </span>
        <CustomPopover
          id="ticketRestaurant-cardOrders-payment-newCreditCards-popover"
          placement="topRight"
          title={false}
          content={
            <div
              style={{
                width: '300px',
                color: '#3f4957',
                padding: '7px 18px',
              }}
            >
              <Text t="ifYouSelectThisOptionTheTotalBalance" />
            </div>
          }
          trigger="hover"
          arrowPointAtCenter={true}
        >
          <div
            style={{
              marginRight: '24px',
              marginBottom: '7px',
            }}
          >
            <CustomImage src={iconInfo} />
          </div>
        </CustomPopover>
      </button>
    );

  return (
    <>
      <ParentElement handleRegisteredCreditCard={handleAnotherCreditCard}>
        <>
          {!!activeCard && (
            <>
              <div className={'unlimited-card-text'}>
                <Text t="yourCardWithAutomaticPaymentInstruction" />
              </div>
              {isMobil ? (
                <div className="unlimited-card">
                  <Radio.Group
                    id="ticketRestaurant-cardOrders-payment-savedCreditCards-custom-radioGroup-input-credit-card"
                    onChange={onChange}
                    value={selectedStoredCard}
                    className="custom-radio-input-credit-card"
                  >
                    <CreditCards cardItem={activeCard} />
                  </Radio.Group>
                </div>
              ) : (
                <div className="unlimited-card">
                  <div className="credit-card-list d-flex">
                    <Radio.Group
                      id="ticketRestaurant-cardOrders-payment-savedCreditCards-custom-radioGroup-input-credit-card2"
                      onChange={onChange}
                      value={selectedStoredCard}
                      className="custom-radio-input-credit-card"
                    >
                      <div key={'credit-card-list-radio-group'}>
                        <Space direction="horizontal">
                          <CreditCards cardItem={activeCard} />
                        </Space>
                      </div>
                    </Radio.Group>
                  </div>
                </div>
              )}
            </>
          )}

          {storedCardFilter.length > 0 && (
            <>
              {!!activeCard && <hr className={'stored-cards-line'} />}
              <div
                className={'stored-cards-text'}
                style={{
                  marginTop: getMarginTop(),
                }}
              >
                {hasOtherCards ? <Text t="otherRegisteredCards" /> : <Text t="registeredCards" />}
              </div>
            </>
          )}

          {isMobil === true ? (
            <Radio.Group
              id="ticketRestaurant-cardOrders-payment-savedCreditCards-custom-radioGroup-input-credit-card3"
              onChange={onChange}
              value={selectedStoredCard}
              className="custom-radio-input-credit-card"
            >
              <Swiper
                slidesPerView="auto"
                slideToClickedSlide={true}
                spaceBetween={isMobil ? 16 : 40}
                className="mySwiper stored-card-wrapper"
              >
                {storedCardFilter.map((item, index) => {
                  return (
                    <SwiperSlide key={item?.id}>
                      <CreditCards cardItem={item} />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </Radio.Group>
          ) : (
            <div className="credit-card-list">
              <Radio.Group
                id="ticketRestaurant-cardOrders-payment-savedCreditCards-custom-radioGroup-input-credit-card4"
                onChange={onChange}
                value={selectedStoredCard}
                className="custom-radio-input-credit-card"
              >
                <div className="" key={'credit-card-list-radio-group'}>
                  <div className="stored-card-wrapper">
                    {storedCardFilter.map((item, index) => {
                      return (
                        <div className="" key={item?.id}>
                          <CreditCards cardItem={item} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Radio.Group>
            </div>
          )}
        </>
      </ParentElement>
      {afterMount && (
        <div className="d-flex pay-with-email">
          <CustomImage src={iconInfo} className={'info-icon'} />{' '}
          <span style={{ marginLeft: '8px' }}>
            <Text t="fromTheCardYouSelectedTheTotalAmount2" />
          </span>
        </div>
      )}

      <div className={'pay-buttons-where-under-page'}>
        <div className="step-btn-container">
          <Button
            id="cardOrders-payment-savedCreditCards-back-btn"
            className="back-btn"
            onClick={() => dispatch(prevStep())}
          >
            <CustomImage src={iconBack} />
            <Text t="turnBack" />
          </Button>
        </div>
        {showPayLater && !afterMount && renderPayLaterButton()}
        <Button
          id="cardOrders-payment-savedCreditCards-payment-btn"
          disabled={!selectedStoredCard}
          onClick={() => window.handlePayment()}
          className="payment-btn"
        >
          {getPaymentButtonText()}
        </Button>
      </div>
    </>
  );
};

export default SavedCreditCards;

const ParentElement = ({ children, handleRegisteredCreditCard }) => {
  return (
    <div className={'saved-credit-cards-card'}>
      <CustomCollapseCard
        headerRightInfo={
          <div className="header-btn" onClick={handleRegisteredCreditCard}>
            <Text t="iWantToPayWithAnotherCard" />
          </div>
        }
        cardTitle={
          <div className="title">
            <Text t="savedCreditCardsTitle" />
          </div>
        }
      >
        <div className="custom-card-body">
          <div className="credit-cards-list-content">{children}</div>
        </div>
      </CustomCollapseCard>
    </div>
  );
};
